import {
  NewOrderAttributes,
  NewOrderType,
  TNewOrderAttributes,
  TNewOrderCountry,
  TNewOrderType,
  TViewsKeyword,
} from "@/entities/new-order";
import { getAppStore } from "@/shared/helpers";
import { COUNTRIES_LIST } from "../../../Constants/CountriesList";
import { IAppItem, IClientData } from "../../../Models/ApiModels";
import {
  getDatesBetween,
  getDateWithoutGTMOffset,
} from "../../../Utils/DateUtils";
import { ICountryModel, INewCampaignObject } from "../Models/CountryModel";

export const getDatesListFromCountry = (country: ICountryModel): string[] => {
  if (!country?.keywords || typeof country.keywords !== "object") {
    return [];
  }
  return Object.keys(country.keywords);
};
//возвращает массив дат из объекта страны

export const getKeysFromCountry = (country: ICountryModel): string[] => {
  if (!country?.keywords || typeof country.keywords !== "object") {
    return [];
  }

  const dates = Object.keys(country.keywords);
  if (dates.length === 0) {
    return [];
  }

  const firstDate = dates[0];
  const firstDateKeywords = country.keywords[firstDate];

  if (!firstDateKeywords || typeof firstDateKeywords !== "object") {
    return [];
  }

  return Object.keys(firstDateKeywords);
};
//возвращает массив ключей из первого дня объекта keywords у страны

export const checkLengthOfKeywords = (country: ICountryModel): boolean => {
  if (!country?.keywords || typeof country.keywords !== "object") {
    return false;
  }
  return !!Object.keys(country.keywords).length;
};
//проверяет существование массива ключей

export const getFirstCountryDate = (country: ICountryModel): string | null => {
  const dates = getDatesListFromCountry(country);
  return dates.length > 0 ? dates[0] : null;
};
//функция возвращает первую дату страны

export const getFirstCountryKeywordValue = (
  country: ICountryModel,
  key: string,
): number => {
  const firstDate = getFirstCountryDate(country);
  if (!firstDate) return 0;

  const dateKeywords = country.keywords[firstDate];
  if (!dateKeywords || Array.isArray(dateKeywords)) {
    return 0;
  }
  const value = (dateKeywords as Record<string, number>)[key];
  return value !== undefined && value !== null ? Number(value) : 0;
};

export const getNewDateArray = (
  firstDay: string,
  startDate: string,
  endDate: string,
) => {
  const dateArray = [];
  const currentDate = getDateWithoutGTMOffset(new Date(firstDay));
  const numberOfDays = getDatesBetween(startDate, endDate).length;

  for (let i = 0; i < numberOfDays; i++) {
    const year = currentDate.getFullYear();
    const month = String(currentDate.getMonth() + 1).padStart(2, "0");
    const day = String(currentDate.getDate()).padStart(2, "0");

    dateArray.push(`${year}-${month}-${day}`);

    currentDate.setDate(currentDate.getDate() + 1);
  }

  return dateArray;
};

export const getTotalKeywordsCountFromCountries = (
  countriesList: TNewOrderCountry[],
) => {
  //получение кол-ва ключей в странах
  let keywordCountTemp = 0;
  if (countriesList.length > 0) {
    for (const country of countriesList) {
      const keywords: Record<
        string,
        number | { installs: number; views: number }
      >[] = Object.values(country.keywords);

      for (const keywordObj of keywords) {
        const values = Object.values(keywordObj);
        keywordCountTemp += values.reduce((acc: number, value) => {
          if (typeof value === "number") {
            return acc + Number(value);
          } else if (typeof value === "object" && "installs" in value) {
            return acc + Number(value.installs);
          } else if (typeof value === "object" && "review" in value) {
            return acc + 1;
          } else {
            return 0;
          }
        }, 0);
      }
    }
    return keywordCountTemp;
  } else {
    return 0;
  }
};

export const getTotalViewsCount = (countriesList: TNewOrderCountry[]) => {
  if (!countriesList.length) {
    return 0;
  }

  let viewsCount = 0;
  for (const country of countriesList) {
    const keywords: TViewsKeyword[] = Object.values(country.keywords);

    for (const keywordObj of keywords) {
      const values = Object.values(keywordObj);
      viewsCount += values.reduce((acc: number, value) => {
        if (typeof value === "object" && "views" in value) {
          return acc + Number(value.views);
        } else {
          return 0;
        }
      }, 0);
    }
  }
  return viewsCount;
};

const getLaunchCost = (
  launchCost: number,
  attributesList: TNewOrderAttributes[],
) => {
  const launchAttributesCount = attributesList.reduce((acc, attribute) => {
    if (attribute.includes("day")) {
      return (acc += 1);
    } else {
      return acc;
    }
  }, 0);

  if (!launchAttributesCount) {
    return 0;
  }

  if (launchAttributesCount === 1) {
    return launchAttributesCount * launchCost;
  } else {
    return (launchAttributesCount - 1) * launchCost;
  }
};

const getRetentionCost = (
  retentionCost: number,
  attributesList: TNewOrderAttributes[],
) => {
  if (attributesList.includes(NewOrderAttributes.Retention)) {
    return retentionCost ?? 0;
  } else {
    return 0;
  }
};

const getClientInstallsCost = (
  appInfo: IAppItem,
  userInfo: IClientData,
  type: TNewOrderType,
  attributes: TNewOrderAttributes[],
) => {
  if (!type) {
    return 0;
  }

  const store = getAppStore(appInfo.url);

  const installCost =
    store === "apple" ? userInfo.a_install_cost : userInfo.install_cost;
  const rateCost =
    store === "apple" ? userInfo.a_rate_cost : userInfo.rate_cost;

  const launchCost = getLaunchCost(userInfo.cost_relaunch, attributes);
  const retentionCost = getRetentionCost(userInfo.retention_cost, attributes);

  if (type === NewOrderType.rate) {
    return rateCost + launchCost + retentionCost;
  } else {
    return installCost + launchCost + retentionCost;
  }
};

export const getTotalOrderCost = (
  appInfo: IAppItem,
  userInfo: IClientData,
  countriesList: TNewOrderCountry[],
  type: TNewOrderType,
  attributes: TNewOrderAttributes[],
) => {
  let totalOrderCost = 0;

  totalOrderCost +=
    getClientInstallsCost(appInfo, userInfo, type, attributes) *
    getTotalKeywordsCountFromCountries(countriesList);

  if (type === NewOrderType.install_with_views) {
    totalOrderCost += getTotalViewsCount(countriesList) * userInfo.a_view_cost;
  }

  return totalOrderCost;
};

export const getTotalCostValue = (
  app: IAppItem,
  type: string | null,
  user: IClientData | null,
  countriesList: TNewOrderCountry[],
  attributesList: TNewOrderAttributes[],
  retentionCost: number = 0,
) => {
  if (app && type && user) {
    if (app) {
      if (app.url.includes("apple")) {
        if (type === "install" || type === "install_bp") {
          return (
            getTotalKeywordsCountFromCountries(countriesList) *
            (user.a_install_cost +
              getLaunchCost(user.cost_relaunch, attributesList))
          );
        }
        if (type === "rate") {
          return (
            getTotalKeywordsCountFromCountries(countriesList) *
            (user.a_rate_cost +
              getLaunchCost(user.cost_relaunch, attributesList))
          );
        }
      } else if (app.url.includes("google")) {
        if (type === "install" || type === "install_bp") {
          return (
            getTotalKeywordsCountFromCountries(countriesList) *
            (user.install_cost +
              getLaunchCost(user.cost_relaunch, attributesList) +
              getRetentionCost(retentionCost, attributesList))
          );
        }
        if (type === "rate") {
          return (
            getTotalKeywordsCountFromCountries(countriesList) *
            (user.rate_cost +
              getLaunchCost(user.cost_relaunch, attributesList) +
              getRetentionCost(retentionCost, attributesList))
          );
        }
      }
    }
  }
  return null;
};

export const checkDelayDateIsExist = (
  delayedTime: string,
  dateStart: string,
) => {
  //Проверка выбранного отложенного времени на то истекло оно или нет.
  //возвращает true если время прошло
  const [hours] = delayedTime.split(" ");

  const nowTime = getDateWithoutGTMOffset(new Date()).getTime();

  const orderTime = getDateWithoutGTMOffset(new Date(dateStart)).setHours(
    Number(hours.split(":")[0]),
    Number(hours.split(":")[1]),
  );

  return nowTime >= orderTime;
};

export const validationFinalOrderForm = (
  campaignObject: INewCampaignObject,
  appStore?: string,
) => {
  const errorsList: string[] = [];

  if (!campaignObject.app_id) {
    errorsList.push("error_lack_app");
  }

  if (!campaignObject.type) {
    errorsList.push("error_lack_type");
  }

  if (
    !campaignObject.date_start ||
    campaignObject.date_start === "Invalid date" ||
    !campaignObject.date_stop ||
    campaignObject.date_stop === "Invalid date"
  ) {
    errorsList.push("error_lack_date");
  }

  if (
    campaignObject.delayed_start &&
    campaignObject.date_start &&
    checkDelayDateIsExist(
      campaignObject.delayed_start,
      campaignObject.date_start,
    )
  ) {
    errorsList.push("error_delayed_time_exist");
  }

  if (!campaignObject.keywords.length) {
    errorsList.push("error_lack_countries");
  }

  if (
    !campaignObject.keywords.every(
      (country) =>
        country.geo !== "" &&
        !!COUNTRIES_LIST.find((c) => country.geo === c.ISO),
    )
  ) {
    //todo сделать проверку каждой страны и выводить список неверных полей
    errorsList.push("error_unselect_country");
  }

  if (
    campaignObject.type !== NewOrderType.review &&
    !campaignObject.keywords.every((country) =>
      Object.keys(country.keywords).some((date) =>
        Object.keys(country.keywords[date]).some((key) => {
          if (!Array.isArray(country.keywords[date])) {
            return (
              (country.keywords[date] as Record<string, number>)[key] !== 0
            );
          }
        }),
      ),
    )
  ) {
    errorsList.push("error_no_values_keys");
  }

  if (campaignObject.type === NewOrderType.review) {
    Object.values(campaignObject.keywords).forEach((country) =>
      Object.values(country.keywords).map((keyword) => {
        if (
          keyword["4 star rates"] &&
          keyword["4 star rates"].length === 0 &&
          keyword["5 star rates"] &&
          keyword["5 star rates"].length === 0
        ) {
          errorsList.push("error_no_reviews_for_date");
        }
      }),
    );
  }

  if (campaignObject.type === NewOrderType.review) {
    Object.values(campaignObject.keywords).forEach((country) => {
      Object.values(country.keywords).forEach((keyword) => {
        Object.values(keyword).forEach((reviews) => {
          if (Array.isArray(reviews)) {
            reviews.forEach((review) => {
              if (review.review.length === 0) {
                errorsList.push("error_no_review_text");
              }
            });
          }
        });
      });
    });
  }

  if (campaignObject.type === NewOrderType.review && appStore === "apple") {
    Object.values(campaignObject.keywords).forEach((country) => {
      Object.values(country.keywords).forEach((keyword) => {
        Object.values(keyword).forEach((reviews) => {
          if (Array.isArray(reviews)) {
            reviews.forEach((review) => {
              if (review.title.length === 0) {
                errorsList.push("error_no_title_for_apple_review");
              }
            });
          }
        });
      });
    });
  }

  return errorsList;
};

export const getFileExtension = (filename: string) => {
  return filename.slice(((filename.lastIndexOf(".") - 1) >>> 0) + 2); // Извлекаем расширение файла из его имени
};

export const getTotalCurrentTypeName = (orderType: string) => {
  //функция, которая возвращает правильное отображение типа кампании
  const typeNames: { [key: string]: string } = {
    [NewOrderType.install]: "installs",
    [NewOrderType.rate]: "rates",
    [NewOrderType.install_bp]: "installs",
    [NewOrderType.install_with_views]: "installs",
    [NewOrderType.review]: "reviews",
  };

  return typeNames[orderType];
};
